import type { AdminAccountMetricRow } from '@/api/adminApi';
import {
  advertisingRegionLabel,
  countryCodeToAdvertisingRegion,
  groupByAdvertisingRegion,
  knownMarketplaceAdvertisingRegion,
  type AdvertisingRegionGroup,
} from '@/api/advertisingAccountRegions';
import { currencyForCountry, isSupportedCurrency, type MoneyContext } from '@/utils/formatCurrency';

/** Bucket for profiles with no country and no known marketplace id. */
export const UNKNOWN_METRIC_REGION = 'UNKNOWN';

export type AdminMetricSubtotal = {
  cost: number;
  sales_30d: number;
  currency: string | null;
};

export type AdminMetricSection = AdvertisingRegionGroup<AdminAccountMetricRow> & {
  code: string | null;
  currency: string | null;
  subtotal: AdminMetricSubtotal | null;
};

export function adminMetricRegion(
  row: Pick<AdminAccountMetricRow, 'country_code' | 'marketplace_id'>
): string {
  const country = row.country_code?.trim();
  if (country) return countryCodeToAdvertisingRegion(country);
  return knownMarketplaceAdvertisingRegion(row.marketplace_id) ?? UNKNOWN_METRIC_REGION;
}

export function adminMetricRowMoney(row: AdminAccountMetricRow, region: string): MoneyContext {
  return {
    currencyCode: row.currency_code,
    countryCode: region === UNKNOWN_METRIC_REGION ? null : region,
  };
}

export function resolvedMetricCurrency(row: AdminAccountMetricRow, region: string): string | null {
  const requested = row.currency_code?.trim().toUpperCase() ?? '';
  if (requested && isSupportedCurrency(requested)) return requested;
  if (region === UNKNOWN_METRIC_REGION) return null;
  return currencyForCountry(region);
}

/** Shared row currency when every account agrees; otherwise the country's currency. */
export function adminMetricSectionCurrency(
  region: string,
  rows: AdminAccountMetricRow[]
): string | null {
  const resolved = rows.map((row) => resolvedMetricCurrency(row, region));
  const present = resolved.filter((code): code is string => Boolean(code));
  if (rows.length > 0 && present.length === rows.length && new Set(present).size === 1) {
    return present[0];
  }
  if (region === UNKNOWN_METRIC_REGION) return null;
  return currencyForCountry(region);
}

export function adminMetricSubtotal(
  region: string,
  rows: AdminAccountMetricRow[]
): AdminMetricSubtotal | null {
  if (rows.length === 0) return null;
  const currency = adminMetricSectionCurrency(region, rows);
  const included = rows.filter((row) => resolvedMetricCurrency(row, region) === currency);
  if (included.length === 0) return null;
  return {
    currency,
    cost: included.reduce((sum, row) => sum + row.cost, 0),
    sales_30d: included.reduce((sum, row) => sum + row.sales_30d, 0),
  };
}

export function buildAdminMetricSections(rows: AdminAccountMetricRow[]): AdminMetricSection[] {
  return groupByAdvertisingRegion(rows, adminMetricRegion).map((group) => {
    const unknown = group.id === UNKNOWN_METRIC_REGION;
    return {
      ...group,
      code: unknown ? null : group.id,
      label: unknown ? 'Unknown' : advertisingRegionLabel(group.id),
      currency: adminMetricSectionCurrency(group.id, group.items),
      subtotal: adminMetricSubtotal(group.id, group.items),
    };
  });
}

export function adminMetricPageTotal(
  sections: AdminMetricSection[]
): { cost: number; sales_30d: number; currency: string } | null {
  const currencies = new Set<string>();
  let cost = 0;
  let sales = 0;
  let count = 0;
  for (const section of sections) {
    for (const row of section.items) {
      const currency = resolvedMetricCurrency(row, section.id);
      if (!currency) return null;
      currencies.add(currency);
      if (currencies.size > 1) return null;
      cost += row.cost;
      sales += row.sales_30d;
      count += 1;
    }
  }
  if (count === 0 || currencies.size !== 1) return null;
  const currency = [...currencies][0];
  return { cost, sales_30d: sales, currency };
}
